import { Modal } from 'react-bootstrap';
import React, { useState } from 'react';
import { useParams, Link } from "react-router-dom";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { Plus, Copy } from 'lucide-react';
import Spinner from '../components/Spinner'
import EmptyState from './audio/PodcastsEmptyState'
import PodcastItem from '../components/podcast/PodcastItem';
import PodcastListSkeleton from '../components/skeletons/pages/PodcastListSkeleton';
import { useIntegration } from '../http-hooks/integration/useIntegration';
import { usePodcasts, useDeletePodcast, usePublishPodcast, useUnpublishPodcast } from '../http-hooks/podcast/usePodcast';
import { Card, PageHeader } from '../components/ui/bootstrap';
import spotify from '../assets/spotify.svg';
import applePodcast from '../assets/apple-podcast.svg';
import googlePodcast from '../assets/google-podcast.svg';


function Podcast() {
    const { org_id, id } = useParams();
    const [show, setShow] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showEmbedCodeModal, setShowEmbedCodeModal] = useState(false);
    const [selectedShow, setSelectedShow] = useState(null);

    const { data: integration } = useIntegration({ org_id, integration_id: id });
    const { data: podcasts = [], isLoading } = usePodcasts({ org_id, integration_id: id });
    const deletePodcastMutation = useDeletePodcast({ org_id, integration_id: id });
    const publishPodcastMutation = usePublishPodcast({ org_id, integration_id: id });
    const unpublishPodcastMutation = useUnpublishPodcast({ org_id, integration_id: id });

    const embedCode = `<iframe 
    id="l2it-widget-iframe" 
    src="${process.env.REACT_APP_WIDGET_BASE_URL}/inline.html?org_id=${org_id}&amp;integration_id=${id}&amp;url=https%3A%2F%2Fwww.getlisten2it.com%2Fblog%2Fthe-7-best-content-ideas-generator-tools-for-your-blog%2F&amp;has_paywall=false" 
    style="border:none;width:100%;height:80px;"
>

</iframe>
`;

    const deleteShow = async (podcastId) => {
        await deletePodcastMutation.mutateAsync(podcastId);
        setShowConfirmation(false);
    }

    const publishChannel = (podcast_id) => {
        publishPodcastMutation.mutate(podcast_id);
    };

    const unpublishChannel = (podcast_id) => {
        unpublishPodcastMutation.mutate(podcast_id);
    };

    const PodcastList = () => (
        <div className="d-flex flex-column gap-3">
            {podcasts.map((item, index) => (
                <PodcastItem
                    key={`podcast-${index}`}
                    item={item}
                    index={index}
                    org_id={org_id}
                    id={id}
                    integration={integration}
                    setShow={setShow}
                    setSelectedShow={setSelectedShow}
                    setShowEmbedCodeModal={setShowEmbedCodeModal}
                    setShowConfirmation={setShowConfirmation}
                    publishChannel={() => publishChannel(item.id)}
                    unpublishChannel={() => unpublishChannel(item.id)}
                />
            ))}
        </div>
    );

    return (
        <>
            {/* Embed code modal */}
            <Modal className="modal-playlist-code" show={show} onHide={() => setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Embed Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3" style={{ fontSize: 14 }}>To embed on your website, copy and paste this snippet in the HTML.</p>
                    <div className="position-relative">
                        <CopyToClipboard text={embedCode} onCopy={() => toast.success("Copied to clipboard")}>
                            <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1 position-absolute"
                                style={{
                                    top: 10, right: 10, zIndex: 1,
                                    background: 'rgba(var(--bs-primary-rgb), 0.08)',
                                    border: '1px solid rgba(var(--bs-primary-rgb), 0.2)',
                                    color: 'var(--bs-primary)', borderRadius: 50, fontSize: 12, padding: '4px 12px',
                                }}
                            >
                                <Copy size={13} /> Copy
                            </button>
                        </CopyToClipboard>
                        <SyntaxHighlighter className="rounded-3" language="html">{embedCode}</SyntaxHighlighter>
                    </div>
                    {selectedShow && (
                        <div className="mt-4">
                            <p className="text-muted mb-2" style={{ fontSize: 14 }}>Or link directly to the podcast page:</p>
                            <div className="d-flex align-items-center rounded-3 overflow-hidden" style={{ background: 'rgba(var(--bs-primary-rgb), 0.04)', border: '1px solid rgba(var(--bs-primary-rgb), 0.15)' }}>
                                <code className="flex-grow-1 px-3 py-2 text-truncate" style={{ fontSize: 12, background: 'transparent' }}>
                                    {`${process.env.REACT_APP_PODCAST_LANDING_PAGE_BASE_URL}/${org_id}/${id}/${selectedShow.id}`}
                                </code>
                                <CopyToClipboard text={`${process.env.REACT_APP_PODCAST_LANDING_PAGE_BASE_URL}/${org_id}/${id}/${selectedShow.id}`} onCopy={() => toast.success("Copied to clipboard")}>
                                    <button type="button" className="btn flex-shrink-0" style={{ borderLeft: '1px solid rgba(var(--bs-primary-rgb), 0.15)', borderRadius: 0, padding: '8px 14px', color: 'var(--bs-primary)', fontSize: 12 }}>
                                        <Copy size={13} />
                                    </button>
                                </CopyToClipboard>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-center border-0">
                    <p className="m-0 text-muted" style={{ fontSize: 13 }}>Need help? <a href="mailto:support@getlisten2it.com" style={{ color: 'var(--bs-primary)' }}>support@getlisten2it.com</a></p>
                </Modal.Footer>
            </Modal>

            {/* Delete confirmation modal */}
            <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Podcast Show</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">Are you sure you want to delete this show? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowConfirmation(false)}>Cancel</button>
                    <button
                        type="button"
                        className="btn btn-danger d-flex align-items-center gap-2"
                        onClick={() => deleteShow(selectedShow.id)}
                        disabled={deletePodcastMutation.isLoading}
                    >
                        {deletePodcastMutation.isLoading ? <Spinner color="white" /> : 'Delete'}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* RSS distribute modal */}
            <Modal show={showEmbedCodeModal} onHide={() => setShowEmbedCodeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Distribute via RSS</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedShow && (
                        <div className="mb-4">
                            <p className="text-muted mb-2" style={{ fontSize: 14 }}>Use this RSS feed URL to submit to podcast directories:</p>
                            <div className="d-flex align-items-center rounded-3 overflow-hidden" style={{ background: 'rgba(var(--bs-primary-rgb), 0.04)', border: '1px solid rgba(var(--bs-primary-rgb), 0.15)' }}>
                                <code className="flex-grow-1 px-3 py-2 text-truncate" style={{ fontSize: 12, background: 'transparent' }}>
                                    {`${process.env.REACT_APP_PODCAST_RSS_BASE_URL}/${org_id}/${id}/${selectedShow.id}`}
                                </code>
                                <CopyToClipboard text={`${process.env.REACT_APP_PODCAST_RSS_BASE_URL}/${org_id}/${id}/${selectedShow.id}`} onCopy={() => toast.success("Copied to clipboard")}>
                                    <button type="button" className="btn flex-shrink-0" style={{ borderLeft: '1px solid rgba(var(--bs-primary-rgb), 0.15)', borderRadius: 0, padding: '8px 14px', color: 'var(--bs-primary)', fontSize: 12 }}>
                                        <Copy size={13} />
                                    </button>
                                </CopyToClipboard>
                            </div>
                        </div>
                    )}
                    <p className="text-muted mb-3" style={{ fontSize: 14 }}>Submit your podcast to major platforms using the RSS link above:</p>
                    <div className="d-flex flex-wrap gap-3">
                        {[
                            { platformName: 'Spotify', icon: spotify },
                            { platformName: 'Apple Podcast', icon: applePodcast },
                            { platformName: 'Google Podcast', icon: googlePodcast },
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className="d-flex flex-column align-items-center justify-content-center gap-2 rounded-3 p-3"
                                style={{ width: 100, background: 'rgba(var(--bs-primary-rgb), 0.04)', border: '1px solid rgba(var(--bs-primary-rgb), 0.1)', fontSize: 12, color: 'var(--bs-secondary-color)' }}
                            >
                                <img src={item.icon} alt={item.platformName} width={36} />
                                {item.platformName}
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Page header */}
            <PageHeader
                title="Podcast Shows"
                subtitle="Publish and distribute your audio as podcasts"
                right={
                    <Link to={`/organisation/${org_id}/integration/${id}/podcast/new`}>
                        <button
                            className="btn d-flex align-items-center gap-2 fw-semibold"
                            style={{
                                background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                                color: 'white', borderRadius: 50, fontSize: 13, padding: '8px 20px', border: 'none',
                            }}
                        >
                            <Plus size={16} /> New Podcast
                        </button>
                    </Link>
                }
            />

            {isLoading ? <PodcastListSkeleton /> : (podcasts.length > 0 ? <PodcastList /> : <EmptyState />)}
        </>
    );
}

export default Podcast;

